import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { VKAuthConfig as SocialConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';

const createAuthVKUrl = (socialConfig: SocialConfig) => {
  const params = [
    `client_id=${socialConfig.id}`,
    'display=popup',
    `redirect_uri=${window.location.origin}/login/vkontakte`,
    `scope=${socialConfig.scope}`,
    'response_type=code',
    'v=5.131',
  ].join('&');
  return [socialConfig.auth_url, params].join('?');
};

type AuthVkCodeError = {
  success: false;
  error: {
    error?: string;
    error_reason?: string;
    error_description?: string;
  };
};
type AuthVkCodeMessageSuccess = {
  success: true;
  code: string;
};
type AuthVkCodeMessageResult = AuthVkCodeError | AuthVkCodeMessageSuccess;

type OnSuccess = (payload: AuthVkCodeMessageResult) => void;
type OnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне VK
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода VK
 * 3. после подтверждения доступа, VK перенаправляет на redirect_url из ссылки и добавляет code или ошибки в параметры
 * 4. на странице шаблона (app/views/social_login/vkontakte.haml) скрипт считывает code или ошибки и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу.
 * */
export const useVKAuth = () => {
  /** ссылка для авторизации в ВК */
  /** полученный от ВК код авторизации */
  const authCodeRef = useRef<AuthVkCodeMessageResult | undefined>();
  const authChanel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  /** открыть окно для авторизации через VK */
  const openVKAuthPopup = (url: string): Window | null =>
    window.open(url, '_blank', 'popup');

  /** прослушивание закрытия окна авторизации VK */
  const listenCloseVKAuthPopup =
    (onError: OnError) =>
    (instance: Window | null): void => {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            onError();
          }
        }
      }, 500);
    };

  /** обработка ответа из результирующего окна авторизации через VK */
  const listenVKAuthChanel =
    (onSuccess: OnSuccess, onError: OnError) =>
    (event: AuthVkCodeMessageResult) => {
      if (!event?.success) {
        // eslint-disable-next-line no-console
        console.error('VK AUTH  NOT SUCCESS: ', event);
        onError();
        return;
      }
      authCodeRef.current = event;
      onSuccess(event);
    };

  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от VK
   * */
  const openVKAuthChanel = (listener) => {
    const chanel = new BroadcastChannel('auth-vk-code');
    authChanel.current = chanel;
    chanel.addEventListener('message', listener);
  };

  const closeVKAuthChanel = () => {
    authChanel.current?.close();
  };

  const handleVKLogin = (
    socialConfig: SocialConfig,
    onSuccess: OnSuccess,
    onError: OnError,
  ): void => {
    try {
      closeVKAuthChanel();
      resetAuthCodeRef();

      const authURL = createAuthVKUrl(socialConfig);
      listenCloseVKAuthPopup(onError)(openVKAuthPopup(authURL));
      openVKAuthChanel(listenVKAuthChanel(onSuccess, onError));
    } catch (e) {
      console.log(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => closeVKAuthChanel, []);

  return {
    handleVKLogin,
  };
};
